import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import { STATUS_AVALIACAO_OPCOES, STATUS_PRAZO_LABELS, HOSPITALAR_LABELS } from '../../utils/acreditacaoOpcoes';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import ArvoreSelecaoRequisitos from './ArvoreSelecaoRequisitos';
import * as catalogoApi from '../../api/catalogo';
import * as hospitaisApi from '../../api/hospitais';
import * as avaliacoesApi from '../../api/avaliacoes';

const FILTROS_STATUS = [
  { value: 'todos', label: 'Todos' },
  { value: 'nao_avaliado', label: 'Não avaliado' },
  ...STATUS_AVALIACAO_OPCOES.map((o) => ({ value: o.value, label: o.label })),
];

const FILTROS_VIGENCIA = [
  { value: 'todos', label: 'Todos' },
  { value: 'em_dia', label: STATUS_PRAZO_LABELS.em_dia },
  { value: 'a_vencer', label: STATUS_PRAZO_LABELS.a_vencer },
  { value: 'vencido', label: STATUS_PRAZO_LABELS.vencido },
];

const FILTROS_OBRIGATORIEDADE = [
  { value: 'todos', label: 'Todos' },
  ...Object.entries(HOSPITALAR_LABELS).map(([value, label]) => ({ value, label })),
];

export default function AbaChecklist({ idHospital, selecionados, onSelecionadosChange }) {
  const [secoes, setSecoes] = useState(null);
  const [escopo, setEscopo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState(null);
  const [filtroCore, setFiltroCore] = useState('todos'); // 'todos' | 'core'
  const [filtroObrigatoriedade, setFiltroObrigatoriedade] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroVigencia, setFiltroVigencia] = useState('todos');
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');
  const requisicaoAtual = useRef(0);
  const buscaAtiva = busca.trim().length >= 2;

  const carregar = async () => {
    const minhaRequisicao = ++requisicaoAtual.current;
    setErro('');
    try {
      const [resCatalogo, resEscopo, resAvaliacoes] = await Promise.all([
        catalogoApi.listarHierarquia(),
        hospitaisApi.obterEscopo(idHospital),
        avaliacoesApi.listarAtuais(idHospital),
      ]);
      if (requisicaoAtual.current !== minhaRequisicao) return; // resposta obsoleta (hospital já trocou)
      setSecoes(resCatalogo.data);
      setEscopo(resEscopo.data);
      setAvaliacoes(resAvaliacoes.data);
    } catch (err) {
      if (requisicaoAtual.current !== minhaRequisicao) return;
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os requisitos para o checklist.'));
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital]);

  const ativosPorId = useMemo(() => {
    const mapa = new Map();
    (escopo || []).forEach((e) => mapa.set(e.id_requisito, e.ativo));
    return mapa;
  }, [escopo]);

  const avaliacaoPorRequisito = useMemo(() => {
    const mapa = new Map();
    (avaliacoes || []).forEach((a) => mapa.set(a.id_requisito, a));
    return mapa;
  }, [avaliacoes]);

  const passaFiltros = (r) => {
    if (!ativosPorId.get(r.id_requisito)) return false;
    if (filtroCore === 'core' && !r.core) return false;
    const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
    if (filtroStatus !== 'todos') {
      const statusAtual = avaliacao ? avaliacao.status : 'nao_avaliado';
      if (statusAtual !== filtroStatus) return false;
    }
    if (filtroVigencia !== 'todos') {
      if (!avaliacao || avaliacao.status_vigencia !== filtroVigencia) return false;
    }
    if (buscaAtiva) {
      const alvo = `${r.codigo} ${r.texto}`.toLowerCase();
      if (!alvo.includes(busca.trim().toLowerCase())) return false;
    }
    return true;
  };

  const arvoreFiltrada = useMemo(() => {
    if (!secoes) return [];
    return secoes
      .map((secao) => {
        const subsecoes = secao.subsecoes
          .filter((sub) => filtroObrigatoriedade === 'todos' || sub.hospitalar === filtroObrigatoriedade)
          .map((sub) => ({ sub, requisitos: requisitosDaSubsecao(sub).filter(passaFiltros) }))
          .filter((s) => s.requisitos.length > 0);
        return { secao, subsecoes };
      })
      .filter((s) => s.subsecoes.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secoes, ativosPorId, avaliacaoPorRequisito, filtroCore, filtroObrigatoriedade, filtroStatus, filtroVigencia, busca]);

  // A seleção só existe para o que está visível na árvore filtrada — ao mudar um filtro (ex:
  // Status = Não avaliado), requisitos que saem de vista também saem da seleção, senão o
  // checklist gerado continuaria incluindo itens marcados antes, escondidos pelo filtro atual.
  useEffect(() => {
    const idsVisiveis = new Set(
      arvoreFiltrada.flatMap((s) => s.subsecoes.flatMap((ss) => ss.requisitos.map((r) => r.id_requisito)))
    );
    const podados = new Set([...selecionados].filter((id) => idsVisiveis.has(id)));
    if (podados.size !== selecionados.size) {
      onSelecionadosChange(podados);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arvoreFiltrada]);

  if (!secoes || !escopo || !avaliacoes) {
    return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;
  }

  const idsFiltrados = arvoreFiltrada.flatMap((s) => s.subsecoes.flatMap((ss) => ss.requisitos.map((r) => r.id_requisito)));

  const marcarTodosFiltrados = () => onSelecionadosChange(new Set(idsFiltrados));
  const limparSelecao = () => onSelecionadosChange(new Set());

  const gerarChecklist = () => {
    document.body.classList.add('imprimir-checklist');
    window.print();
  };

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
        <p style={{ fontSize: 13, color: 'var(--am-cinza-texto-2)', margin: 0, maxWidth: 480 }}>
          Use os filtros para localizar os requisitos do setor e marque na árvore abaixo o que
          deve entrar no checklist impresso. Marcar uma Seção ou Subseção marca todos os
          requisitos dentro dela — cada requisito também pode ser marcado individualmente. Mudar
          um filtro remove da seleção o que sai de vista.
        </p>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button type="button" className="am-btn am-btn-outline" onClick={marcarTodosFiltrados} disabled={idsFiltrados.length === 0}>
            <span className="ms">check_box</span>
            Marcar todos os filtrados
          </button>
          <button type="button" className="am-btn am-btn-outline" onClick={limparSelecao} disabled={selecionados.size === 0}>
            <span className="ms">check_box_outline_blank</span>
            Limpar seleção
          </button>
          <button type="button" className="am-btn am-btn-dark" disabled={selecionados.size === 0} onClick={gerarChecklist}>
            <span className="ms">checklist</span>
            Gerar Checklist{selecionados.size ? ` (${selecionados.size})` : ''}
          </button>
        </div>
      </div>

      <label className="am-field" style={{ maxWidth: 420, marginBottom: 14 }}>
        <div className="am-field__label-row">
          <span className="am-field__label">Buscar requisito</span>
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Código ou texto do requisito..."
        />
      </label>

      <div className="filtros-painel mb-3">
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Escopo</span>
          <div className="filter-tabs">
            <button
              type="button"
              className={`filter-tab${filtroCore === 'todos' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('todos')}
            >
              Todos
            </button>
            <button
              type="button"
              className={`filter-tab${filtroCore === 'core' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('core')}
            >
              Somente CORE
            </button>
          </div>
          <span className="filtros-painel__divisor" />
          <div className="filter-tabs">
            {FILTROS_OBRIGATORIEDADE.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroObrigatoriedade === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroObrigatoriedade(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Status</span>
          <div className="filter-tabs">
            {FILTROS_STATUS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroStatus === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroStatus(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Vigência</span>
          <div className="filter-tabs">
            {FILTROS_VIGENCIA.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`filter-tab${filtroVigencia === f.value ? ' is-active' : ''}`}
                onClick={() => setFiltroVigencia(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {arvoreFiltrada.length === 0 ? (
        <div className="empty-state">
          <div className="ms">checklist</div>
          <div>Nenhum requisito encontrado com esses filtros.</div>
        </div>
      ) : (
        <ArvoreSelecaoRequisitos
          arvore={arvoreFiltrada}
          selecionados={selecionados}
          onChange={onSelecionadosChange}
          avaliacaoPorRequisito={avaliacaoPorRequisito}
          expandirTudo={buscaAtiva}
        />
      )}
    </div>
  );
}
